import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  DxButtonModule,
  DxDataGridModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTextBoxModule,
} from 'devextreme-angular';
import { environment } from 'src/environments/environment';
import { CrudService } from '../../../services/crud.service';
import { StatusBadgeComponent } from '../../shared/status-badge/status-badge.component';

/** One connected-supplier directory row (the {@link CustomerSupplierController::customerSuppliers} DTO). */
export interface ConnectedSupplier {
  supplier_code: string;
  supplier_name: string;
  /** Contact email held by Promoflo for notifications; may be null. */
  email: string | null;
  /** In-flight reviews (pending_review + update_requested_awaiting_customer) for this supplier. */
  active_reviews_count: number;
  /** Literal "Active" — the directory lists active connected suppliers only (FRD §5.3). */
  status: string;
  /** Date-only latest supplier-invitation acceptance date; null until acceptance history exists. */
  joined_date: string | null;
}

/** An eligible, tenant-scoped supplier returned by the invitation candidate endpoint. */
export interface SupplierInvitationCandidate {
  id: number;
  supplier_code: string;
  supplier_name: string;
}

/** The pending invitation details returned after the API has accepted email delivery. */
interface SupplierInvitationResult {
  supplier_id: number;
  email: string;
  status: 'pending';
  expires_at?: string;
}

export interface InvitationOutcome {
  kind: 'success' | 'error';
  message: string;
}

/**
 * Customer Portal — Connected Suppliers directory (Story 3.7, FR14 / UC-PROMO-009 / AC-SM-01).
 *
 * A READ-ONLY directory of the customer's connected suppliers, built from FRD §5.3 (there is no
 * Suppliers section in the design spec / prototype). Columns: Supplier Name / Contact Email /
 * Active Reviews / Status ("Active" badge) / Joined. There are NO disconnect / edit / delete row
 * actions — a pending invitation is not a connected supplier and never mutates this directory.
 * Both "+ Connect Supplier" actions open the same in-page invitation popup.
 *
 * `%`/$-free by construction — the directory is supplier-shaped, carrying no discount values (BR-07).
 * Theme/`--dx-*` tokens only; Australian English; WCAG 2.1 AA (grid semantics, keyboard-navigable
 * link/button, visible focus, aria-labels).
 */
@Component({
  selector: 'app-customer-suppliers',
  templateUrl: './suppliers.component.html',
  styleUrls: ['../../shared/pricing-layout.css', './suppliers.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    DxButtonModule,
    DxDataGridModule,
    DxPopupModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    StatusBadgeComponent,
  ],
  standalone: true,
})
export class SuppliersComponent implements OnInit {
  /** Directory rows; empty array drives the empty state. */
  suppliers: ConnectedSupplier[] = [];

  loading = true;
  loadError = false;

  invitationVisible = false;
  invitationSubmitting = false;
  candidatesLoading = false;
  candidateLoadError = false;
  candidates: SupplierInvitationCandidate[] = [];
  selectedSupplierId: number | null = null;
  invitationEmail = '';
  supplierValidationError = '';
  emailValidationError = '';
  invitationOutcome: InvitationOutcome | null = null;

  readonly candidateSearchExpressions = ['supplier_name', 'supplier_code'];

  private apiUrl: string = environment.baseApiUrl;
  private readonly endpoint = `${this.apiUrl}customer/suppliers`;
  private readonly candidateEndpoint = `${this.apiUrl}customer/suppliers/candidates`;
  private readonly invitationEndpoint = `${this.apiUrl}customer/supplier-invitations`;
  private candidatesLoaded = false;

  constructor(private crudService: CrudService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  /** Fetch the connected-supplier directory. */
  private async load(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    try {
      const response: any = await firstValueFrom(this.crudService.newGetData(this.endpoint));
      this.suppliers = (Array.isArray(response) ? response : response?.data ?? []) as ConnectedSupplier[];
    } catch (error) {
      console.error('Failed to load connected suppliers', error);
      this.loadError = true;
      this.suppliers = [];
    } finally {
      this.loading = false;
    }
  }

  /** True once the directory has loaded with no connected suppliers (drives the empty state). */
  get isEmpty(): boolean {
    return !this.loading && !this.loadError && this.suppliers.length === 0;
  }

  /** Open the shared invitation popup and load its local candidate data once. */
  async openInvitation(): Promise<void> {
    if (this.invitationSubmitting) return;
    this.resetInvitationForm();
    this.invitationVisible = true;
    if (!this.candidatesLoaded && !this.candidatesLoading) {
      await this.loadCandidates();
    }
  }

  /** Retry only the candidate lookup; connected-directory loading state remains untouched. */
  async retryCandidates(): Promise<void> {
    if (this.candidatesLoading) return;
    await this.loadCandidates();
  }

  /** Close and clear a retryable popup. An in-flight command cannot be interrupted. */
  cancelInvitation(): void {
    if (this.invitationSubmitting) return;
    this.resetInvitationForm();
    this.invitationVisible = false;
  }

  /** Prevent Escape, the popup close button, or any other hide path while a command is in flight. */
  onInvitationHiding(event: { cancel?: boolean }): void {
    // A successful response is the one in-flight path allowed to close the popup programmatically.
    if (this.invitationSubmitting && this.invitationOutcome?.kind !== 'success') {
      event.cancel = true;
      return;
    }
    if (this.invitationOutcome?.kind !== 'success') {
      this.resetInvitationForm();
    }
  }

  candidateDisplay(candidate: SupplierInvitationCandidate | null | undefined): string {
    return candidate ? `${candidate.supplier_name} (${candidate.supplier_code})` : '';
  }

  get supplierInputAttributes(): Record<string, string> {
    const attributes: Record<string, string> = {
      id: 'supplier-invitation-supplier',
      'aria-label': 'Supplier',
      'aria-invalid': this.supplierValidationError ? 'true' : 'false',
    };
    if (this.supplierValidationError) {
      attributes['aria-describedby'] = 'supplier-invitation-supplier-error';
    }
    return attributes;
  }

  get emailInputAttributes(): Record<string, string> {
    const attributes: Record<string, string> = {
      id: 'supplier-invitation-email',
      autocomplete: 'email',
      'aria-label': 'Supplier contact email',
      'aria-invalid': this.emailValidationError ? 'true' : 'false',
    };
    if (this.emailValidationError) {
      attributes['aria-describedby'] = 'supplier-invitation-email-error';
    }
    return attributes;
  }

  onSupplierChanged(event: { value?: number | null }): void {
    this.selectedSupplierId = event.value ?? null;
    this.supplierValidationError = '';
    this.clearErrorOutcome();
  }

  onEmailChanged(event: { value?: string | null }): void {
    this.invitationEmail = event.value ?? '';
    this.emailValidationError = '';
    this.clearErrorOutcome();
  }

  /** Route click and Enter submission through the same re-entry-guarded command handler. */
  onInvitationSubmit(event: Event): void {
    event.preventDefault();
    void this.sendInvitation();
  }

  /** Validate and issue exactly one invitation command. The synchronous guard rejects re-entry. */
  async sendInvitation(): Promise<void> {
    if (this.invitationSubmitting || !this.validateInvitation()) return;

    const supplierId = this.selectedSupplierId as number;
    const trimmedEmail = this.invitationEmail.trim();
    this.invitationSubmitting = true;
    this.invitationOutcome = null;

    try {
      const response: any = await firstValueFrom(this.crudService.postCommand(
        this.invitationEndpoint,
        { supplier_id: supplierId, email: trimmedEmail },
      ));
      const result = this.pendingInvitationResult(response, supplierId);
      if (!result) {
        this.invitationOutcome = {
          kind: 'error',
          message: "We couldn't confirm that the invitation was sent. Please try again.",
        };
        return;
      }

      this.candidates = this.candidates.filter(({ id }) => id !== supplierId);
      this.resetInvitationForm(false);
      this.invitationOutcome = {
        kind: 'success',
        message: `Invitation sent to ${result.email}. The link is valid for seven days. The supplier is not connected until they accept it.`,
      };
      this.invitationVisible = false;
    } catch (error: any) {
      console.error('Failed to send supplier invitation', error);
      if (error?.status === 404 || error?.status === 409) {
        this.invalidateCandidate(supplierId);
      }
      this.invitationOutcome = { kind: 'error', message: this.invitationErrorMessage(error) };
    } finally {
      this.invitationSubmitting = false;
    }
  }

  private async loadCandidates(): Promise<void> {
    this.candidatesLoading = true;
    this.candidateLoadError = false;
    try {
      const response: unknown = await firstValueFrom(this.crudService.newGetData(this.candidateEndpoint));
      this.candidates = this.parseCandidates(response);
      this.candidatesLoaded = true;
    } catch (error) {
      console.error('Failed to load supplier invitation candidates', error);
      this.candidates = [];
      this.candidatesLoaded = false;
      this.candidateLoadError = true;
    } finally {
      this.candidatesLoading = false;
    }
  }

  private validateInvitation(): boolean {
    const supplierId = this.selectedSupplierId;
    this.supplierValidationError = Number.isInteger(supplierId)
      && (supplierId as number) > 0
      && this.candidates.some(({ id }) => id === supplierId)
      ? ''
      : 'Select an eligible supplier.';

    const email = this.invitationEmail.trim();
    if (!email) {
      this.emailValidationError = 'Enter a contact email.';
    } else if (email.length > 255) {
      this.emailValidationError = 'Email must be 255 characters or fewer.';
    } else if (!this.isValidEmail(email)) {
      this.emailValidationError = 'Enter a valid email address.';
    } else {
      this.emailValidationError = '';
    }

    return !this.supplierValidationError && !this.emailValidationError;
  }

  private resetInvitationForm(clearOutcome = true): void {
    this.selectedSupplierId = null;
    this.invitationEmail = '';
    this.supplierValidationError = '';
    this.emailValidationError = '';
    if (clearOutcome) this.invitationOutcome = null;
  }

  private clearErrorOutcome(): void {
    if (this.invitationOutcome?.kind === 'error') this.invitationOutcome = null;
  }

  private parseCandidates(response: unknown): SupplierInvitationCandidate[] {
    if (!this.isRecord(response) || response['success'] !== true || !Array.isArray(response['data'])) {
      throw new Error('Malformed supplier invitation candidate response');
    }

    const seenIds = new Set<number>();
    return response['data'].map((row: unknown) => {
      if (!this.isRecord(row)) throw new Error('Malformed supplier invitation candidate');

      const id = row['id'];
      const supplierCode = row['supplier_code'];
      const supplierName = row['supplier_name'];
      if (
        !Number.isInteger(id)
        || (id as number) <= 0
        || seenIds.has(id as number)
        || typeof supplierCode !== 'string'
        || !supplierCode.trim()
        || typeof supplierName !== 'string'
        || !supplierName.trim()
      ) {
        throw new Error('Malformed supplier invitation candidate');
      }

      seenIds.add(id as number);
      return {
        id: id as number,
        supplier_code: supplierCode.trim(),
        supplier_name: supplierName.trim(),
      };
    });
  }

  private pendingInvitationResult(response: unknown, supplierId: number): SupplierInvitationResult | null {
    if (!this.isRecord(response) || response['success'] !== true || !this.isRecord(response['data'])) {
      return null;
    }

    const data = response['data'];
    const email = typeof data['email'] === 'string' ? data['email'].trim() : '';
    if (data['status'] !== 'pending' || data['supplier_id'] !== supplierId || !email) {
      return null;
    }

    return {
      supplier_id: supplierId,
      email,
      status: 'pending',
      ...(typeof data['expires_at'] === 'string' ? { expires_at: data['expires_at'] } : {}),
    };
  }

  private invalidateCandidate(supplierId: number): void {
    this.candidates = this.candidates.filter(({ id }) => id !== supplierId);
    if (this.selectedSupplierId === supplierId) {
      this.selectedSupplierId = null;
      this.supplierValidationError = '';
    }
  }

  private isValidEmail(email: string): boolean {
    if (email.length > 255 || email.includes('..')) return false;

    const atIndex = email.indexOf('@');
    if (atIndex <= 0 || atIndex !== email.lastIndexOf('@')) return false;

    const localPart = email.slice(0, atIndex);
    const domain = email.slice(atIndex + 1);
    if (
      localPart.length > 64
      || localPart.startsWith('.')
      || localPart.endsWith('.')
      || !/^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/.test(localPart)
      || domain.length > 253
      || !domain.includes('.')
    ) {
      return false;
    }

    return domain.split('.').every((label) => (
      label.length > 0
      && label.length <= 63
      && /^[A-Za-z0-9-]+$/.test(label)
      && !label.startsWith('-')
      && !label.endsWith('-')
    ));
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private invitationErrorMessage(error: any): string {
    switch (error?.status) {
      case 400:
        return 'Check the supplier and email, then try again.';
      case 401:
        return 'Your session is no longer authorised. Sign in again and retry.';
      case 404:
        return 'This supplier is no longer available to invite. Choose another supplier and try again.';
      case 409: {
        const message = typeof error?.error?.message === 'string' ? error.error.message : '';
        if (message.toLowerCase().includes('already connected')) {
          return 'This supplier is already connected and cannot be invited.';
        }
        if (message.toLowerCase().includes('invitation already exists')) {
          return 'A pending invitation already exists for this supplier.';
        }
        return 'This supplier cannot be invited because it is already connected or has a pending invitation.';
      }
      case 500:
        return 'The invitation could not be delivered. Please try again.';
      default:
        return "We couldn't send the invitation. Check your connection and try again.";
    }
  }
}
